/**
 * Media Overlay Component
 * Creates a modal overlay with swiper for product images
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Zoom, Thumbs } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import 'swiper/css/thumbs';

export default class MediaOverlay {
  constructor() {
    this.overlay = null;
    this.swiper = null;
    this.thumbsSwiper = null;
    this.isOpen = false;
    this.init();
  }

  init() {
    // Find existing overlay in DOM (rendered by Liquid)
    this.overlay = document.getElementById('media-overlay');
    
    if (!this.overlay) {
      console.error('Media overlay element not found in DOM');
      return;
    }
    
    this.setupEventListeners();
    this.initZoomControls();
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Click handlers for media items (only images)
    document.addEventListener('click', (event) => {
      const mediaItem = event.target.closest('.pdp__media-item');
      
      if (mediaItem) {
        // Only open overlay for media items that contain images (not videos)
        const hasVideo = mediaItem.querySelector('video');
        const hasImage = mediaItem.querySelector('img') || mediaItem.querySelector('picture');
        
        // Only proceed if it has an image AND no video
        if (hasImage && !hasVideo) {
          const isIconOnly = mediaItem.classList.contains('zoom-active-on-icon');

          if (isIconOnly) {
            // Only open overlay if the click was on (or inside) the zoom-trigger icon
            const zoomTrigger = event.target.closest('.zoom-trigger');
            if (!zoomTrigger) return; // Ignore clicks on the image itself
          }

          event.preventDefault();
          event.stopPropagation();
          this.openOverlay(mediaItem);
        }
      }
    });

    // Close button
    const closeButton = this.overlay.querySelector('.media-overlay__close');
    closeButton.addEventListener('click', () => {
      this.closeOverlay();
    });

    // Backdrop click to close
    const backdrop = this.overlay.querySelector('.media-overlay__backdrop');
    backdrop.addEventListener('click', () => {
      this.closeOverlay();
    });

    // Escape key to close
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeOverlay();
      }
    });

    // Prevent body scroll when overlay is open
    this.overlay.addEventListener('wheel', (event) => {
      if (this.isOpen) {
        event.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Initialize zoom controls
   */
  initZoomControls() {
    const zoomInBtn = this.overlay.querySelector('.media-overlay__zoom-in');
    const zoomOutBtn = this.overlay.querySelector('.media-overlay__zoom-out');

    if (!zoomInBtn || !zoomOutBtn) {
      console.error('Zoom controls not found');
      return;
    }

    // Zoom in functionality
    zoomInBtn.addEventListener('click', () => {
      this.zoomIn();
    });

    // Zoom out functionality
    zoomOutBtn.addEventListener('click', () => {
      this.zoomOut();
    });

    // Update button states initially
    this.updateZoomControlsState(1);
  }

  /**
   * Zoom in to the current image
   */
  zoomIn() {
    if (!this.swiper || !this.swiper.zoom) {
      console.error('Swiper or zoom not available');
      return;
    }

    const currentSlide = this.swiper.slides[this.swiper.activeIndex];
    const zoomContainer = currentSlide?.querySelector('.swiper-zoom-container');
    
    if (!zoomContainer) {
      console.error('Zoom container not found in current slide');
      return;
    }

    // Use Swiper's zoom.in method
    this.swiper.zoom.in();
  }

  /**
   * Zoom out of the current image
   */
  zoomOut() {
    if (!this.swiper || !this.swiper.zoom) {
      console.error('Swiper or zoom not available');
      return;
    }

    const currentSlide = this.swiper.slides[this.swiper.activeIndex];
    const zoomContainer = currentSlide?.querySelector('.swiper-zoom-container');
    
    if (!zoomContainer) {
      console.error('Zoom container not found in current slide');
      return;
    }

    // Use Swiper's zoom.out method
    this.swiper.zoom.out();
  }
  /**
   * Get max zoom ratio from data attributes
   * @returns {number} Maximum zoom ratio
   */
  getMaxZoomRatio() {
    const isMobile = window.innerWidth < 768;
    const zoomDesktop = parseFloat(this.overlay.dataset.zoomDesktop) || 3;
    const zoomMobile = parseFloat(this.overlay.dataset.zoomMobile) || 5;
    return isMobile ? zoomMobile : zoomDesktop;
  }
  /**
   * Update zoom controls state based on current zoom level
   * @param {number} scale - Current zoom scale
   */
  updateZoomControlsState(scale) {
    const zoomInBtn = this.overlay.querySelector('.media-overlay__zoom-in');
    const zoomOutBtn = this.overlay.querySelector('.media-overlay__zoom-out');
    const zoomControls = this.overlay.querySelector('.media-overlay__zoom-controls');

    if (!zoomInBtn || !zoomOutBtn || !zoomControls) return;

    const maxRatio = this.getMaxZoomRatio();
    const minRatio = 1;

    // Update button states
    zoomInBtn.disabled = scale >= maxRatio;
    zoomOutBtn.disabled = scale <= minRatio;

    // Add visual feedback classes
    zoomInBtn.classList.toggle('media-overlay__zoom-btn--disabled', scale >= maxRatio);
    zoomOutBtn.classList.toggle('media-overlay__zoom-btn--disabled', scale <= minRatio);

    // Show/hide controls based on zoom capability
    zoomControls.classList.toggle('media-overlay__zoom-controls--visible', true);
  }

  /**
   * Open the overlay with media starting at specific index
   * @param {HTMLElement} clickedItem - The media item that was clicked
   */
  openOverlay(clickedItem) {
    // Get all pre-rendered slides in overlay
    const allSlides = Array.from(this.overlay.querySelectorAll('.media-overlay__slide'));
    
    if (allSlides.length === 0) {
      console.error('No slides found in media overlay');
      return;
    }
    
    // Find the media ID from the clicked item
    const clickedMediaId = clickedItem.dataset.mediaId;
    
    // Find matching slide index by media ID
    let startIndex = 0;
    if (clickedMediaId) {
      const matchingSlideIndex = allSlides.findIndex(slide => 
        slide.dataset.mediaId === clickedMediaId
      );
      
      if (matchingSlideIndex !== -1) {
        startIndex = matchingSlideIndex;
      }
    }
    
    // Ensure index is within bounds
    startIndex = Math.max(0, Math.min(startIndex, allSlides.length - 1));
    
    // Show overlay
    this.showOverlay();
    
    // Initialize swiper with start index
    this.initializeSwiper(startIndex);
    
    // Update counter
    this.updateCounter(startIndex + 1, allSlides.length);
  }

  /**
   * Show the overlay
   */
  showOverlay() {
    this.overlay.classList.add('media-overlay--active');
    document.body.classList.add('media-overlay-open');
    this.isOpen = true;
  }

  /**
   * Initialize the swiper
   * @param {number} startIndex - The index to start at
   */
  initializeSwiper(startIndex = 0) {
    // Destroy existing swipers if they exist
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
    }
    
    // Initialize thumbnails swiper first
    this.thumbsSwiper = new Swiper('.media-overlay__thumbs', {
      modules: [],
      slidesPerView: 'auto',
      spaceBetween: 1,
      watchSlidesProgress: true,
      freeMode: true,
      breakpoints: {
        768: {
          spaceBetween: 1,
        },
      },
    });
    
    const isMobile = window.innerWidth < 768;
    this.swiper = new Swiper('.media-overlay__swiper', {
      modules: [Navigation, Pagination, Zoom, Thumbs],
      initialSlide: startIndex,
      loop: false,
      speed: 300,
      zoom: {
        maxRatio: this.getMaxZoomRatio(),
        minRatio: 1,
        toggle: true,
      },
      thumbs: {
        swiper: this.thumbsSwiper,
      },
      navigation: {
        nextEl: '.media-overlay__nav--next',
        prevEl: '.media-overlay__nav--prev',
      },
      pagination: {
        el: '.media-overlay__pagination',
        clickable: true,
      },
      on: {
        slideChange: () => {
          if (this.swiper && this.swiper.activeIndex !== undefined) {
            const currentIndex = this.swiper.activeIndex;
            const totalSlides = this.swiper.slides.length;
            this.updateCounter(currentIndex + 1, totalSlides);
            
            // Apply 200% zoom to the new slide
            setTimeout(() => {
              this.applyInitialZoom();
            }, 50);
          }
        },
        zoomChange: (swiper, scale) => {
          this.handleZoomChange(scale);
        },
      },
    });
    
    // Set initial zoom to 200% after Swiper is initialized
    setTimeout(() => {
      this.applyInitialZoom();
    }, 100);
  }

  /**
   * Apply initial 200% zoom to current slide
   */
  applyInitialZoom() {
    if (this.swiper && this.swiper.zoom) {
      const currentSlide = this.swiper.slides[this.swiper.activeIndex];
      const zoomContainer = currentSlide?.querySelector('.swiper-zoom-container');
      
      if (zoomContainer) {
        // Manually set the zoom scale to 2 (200%)
        this.swiper.zoom.enable();
        // Use Swiper's internal method to set scale
        const img = zoomContainer.querySelector('img');
        // if (img) {
        //   zoomContainer.style.transform = 'translate3d(0px, 0px, 0px) scale(3)';
        //   this.handleZoomChange(2);
        // }
      }
    }
  }


  /**
   * Handle zoom state changes
   * @param {number} scale - Current zoom scale
   */
  handleZoomChange(scale) {
    const container = this.overlay.querySelector('.media-overlay__container');
    const navigation = this.overlay.querySelectorAll('.media-overlay__nav');
    const pagination = this.overlay.querySelector('.media-overlay__pagination');
    
    if (scale > 1) {
      // Hide navigation when zoomed in
      container.classList.add('media-overlay--zoomed');
      navigation.forEach(nav => nav.style.opacity = '0.3');
      if (pagination) pagination.style.opacity = '0.3';
    } else {
      // Show navigation when zoomed out
      container.classList.remove('media-overlay--zoomed');
      navigation.forEach(nav => nav.style.opacity = '1');
      if (pagination) pagination.style.opacity = '1';
    }

    // Update zoom controls state
    this.updateZoomControlsState(scale);
  }

  /**
   * Update the counter display
   * @param {number} current - Current slide number
   * @param {number} total - Total number of slides
   */
  updateCounter(current, total) {
    const currentEl = this.overlay.querySelector('.media-overlay__current');
    const totalEl = this.overlay.querySelector('.media-overlay__total');
    
    if (currentEl) currentEl.textContent = current;
    if (totalEl) totalEl.textContent = total;
  }


  /**
   * Close the overlay
   */
  closeOverlay() {
    this.overlay.classList.remove('media-overlay--active');
    document.body.classList.remove('media-overlay-open');
    this.isOpen = false;

    // Destroy swipers (but keep slides in DOM)
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }
}

// Auto-initialize when the script loads
const mediaOverlay = new MediaOverlay();

// Export for manual initialization if needed
export { MediaOverlay };